import { track } from "@/lib/analytics/tracker";
import type { SignInModalCloseReason } from "@/lib/analytics/funnel-events";

export type SignInModalView = "start" | "log-in" | "code" | "verify" | "about";
export type SignInModalNotice = "error" | "cancelled" | "refused";
export type SignInModalPurpose = "sign-up" | "log-in";

export interface SignInModalState {
  open: boolean;
  view: SignInModalView;
  notice?: SignInModalNotice;
  purpose?: SignInModalPurpose;
  email?: string;
  code?: string;
}

const CLOSED: SignInModalState = { open: false, view: "start" };

let state: SignInModalState = CLOSED;
let openedAt = 0;
let openedView: SignInModalView = "start";
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeSignInModal(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSignInModalState(): SignInModalState {
  return state;
}

export function getClosedSignInModalState(): SignInModalState {
  return CLOSED;
}

export function openSignInModal(
  options: Omit<Partial<SignInModalState>, "open"> = {},
): void {
  const wasOpen = state.open;
  state = { ...options, open: true, view: options.view ?? "start" };
  if (!wasOpen) {
    openedAt = Date.now();
    openedView = state.view;
    track("auth.modal_opened", { view: state.view, purpose: state.purpose, notice: state.notice });
  }
  emit();
}

export function closeSignInModal(reason: SignInModalCloseReason = "dismissed"): void {
  if (state.open) {
    track("auth.modal_closed", {
      reason,
      opened_view: openedView,
      purpose: state.purpose,
      duration_ms: Date.now() - openedAt,
    });
  }
  state = CLOSED;
  emit();
}

export function readSignInParam(value: string | null): {
  view: SignInModalView;
  notice?: SignInModalNotice;
} | null {
  if (value === "finish") return { view: "about" };
  if (value === "verify") return { view: value };
  if (value === "error" || value === "cancelled" || value === "refused") {
    return { view: "start", notice: value };
  }
  return null;
}
